import { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Switch,
  Slider,
  Badge,
} from '@machinemetrics/mm-react-components';
import {
  Settings,
  Zap,
  AlertTriangle,
  Truck,
  Save,
  RotateCcw,
} from 'lucide-react';
import { useCompany } from './CompanyLayout';
import { companyApi } from '../../services/api';

const DEFAULT_TIME_SCALE = 1.0;
const DEFAULT_OFFLINE_CAP_HOURS = 8;

export default function CompanySettings() {
  const { company } = useCompany();

  // Simulation settings (persisted via API)
  const [simSettings, setSimSettings] = useState({
    timeScale: DEFAULT_TIME_SCALE,
    offlineCapHours: DEFAULT_OFFLINE_CAP_HOURS,
  });

  // Sync from company when loaded (API returns timeScale, offlineCap in seconds)
  useEffect(() => {
    if (!company) return;
    if (company.timeScale != null && Number.isFinite(company.timeScale)) {
      const t = Math.min(10, Math.max(0.5, Number(company.timeScale)));
      setSimSettings((prev) => ({ ...prev, timeScale: t }));
    }
    if (company.offlineCap != null && Number.isFinite(company.offlineCap)) {
      const hours = Math.min(24, Math.max(1, Number(company.offlineCap) / 3600));
      setSimSettings((prev) => ({ ...prev, offlineCapHours: hours }));
    }
  }, [company?.id, company?.timeScale, company?.offlineCap]);

  // Event settings state (not persisted yet)
  const [eventSettings, setEventSettings] = useState({
    machineFaults: { enabled: true, probability: 0.05 },
    supplyShocks: { enabled: true, probability: 0.02 },
    demandSpikes: { enabled: true, probability: 0.03 },
  });

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const handleSave = async () => {
    if (!company?.id) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await companyApi.update(company.id, {
        timeScale: simSettings.timeScale,
        offlineCap: simSettings.offlineCapHours * 3600, // API expects seconds
      });
      setSaveMessage('Settings saved. Time scale and offline cap are updated.');
    } catch (err) {
      setSaveMessage(err?.response?.data?.error || err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setSimSettings({ timeScale: DEFAULT_TIME_SCALE, offlineCapHours: DEFAULT_OFFLINE_CAP_HOURS });
    setEventSettings({
      machineFaults: { enabled: true, probability: 0.05 },
      supplyShocks: { enabled: true, probability: 0.02 },
      demandSpikes: { enabled: true, probability: 0.03 },
    });
    setSaveMessage(null);
  };

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Company Settings</h1>
        <p className="text-muted-foreground">
          Configure simulation and event settings for {company?.name}
        </p>
      </div>

      {/* General Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            General Settings
          </CardTitle>
          <CardDescription>Basic simulation parameters</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input value={company?.name || ''} disabled />
          </div>

          <div className="space-y-2">
            <Label>Mode</Label>
            <Badge variant="outline">{company?.mode}</Badge>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Time Scale</Label>
              <span className="text-sm text-muted-foreground">{simSettings.timeScale}x</span>
            </div>
            <Slider
              value={[simSettings.timeScale]}
              onValueChange={([value]) => setSimSettings({ ...simSettings, timeScale: value })}
              min={0.5}
              max={10}
              step={0.5}
            />
            <p className="text-xs text-muted-foreground">
              Speed up simulation time (1x = real-time)
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Offline Cap (hours)</Label>
              <span className="text-sm text-muted-foreground">{simSettings.offlineCapHours}h</span>
            </div>
            <Slider
              value={[simSettings.offlineCapHours]}
              onValueChange={([value]) => setSimSettings({ ...simSettings, offlineCapHours: value })}
              min={1}
              max={24}
              step={1}
            />
            <p className="text-xs text-muted-foreground">
              Maximum time to simulate when offline
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Event Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Event Configuration
          </CardTitle>
          <CardDescription>
            Control random event probabilities (per simulation hour)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Machine Faults */}
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <div>
                  <h4 className="font-medium">Machine Faults</h4>
                  <p className="text-sm text-muted-foreground">
                    Random machine breakdowns and alarms
                  </p>
                </div>
              </div>
              <Switch
                checked={eventSettings.machineFaults.enabled}
                onCheckedChange={(checked) =>
                  setEventSettings({
                    ...eventSettings,
                    machineFaults: { ...eventSettings.machineFaults, enabled: checked },
                  })
                }
              />
            </div>
            {eventSettings.machineFaults.enabled && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Probability</Label>
                  <span className="text-sm text-muted-foreground">
                    {(eventSettings.machineFaults.probability * 100).toFixed(1)}% per hour
                  </span>
                </div>
                <Slider
                  value={[eventSettings.machineFaults.probability]}
                  onValueChange={([value]) =>
                    setEventSettings({
                      ...eventSettings,
                      machineFaults: { ...eventSettings.machineFaults, probability: value },
                    })
                  }
                  min={0}
                  max={0.2}
                  step={0.01}
                />
              </div>
            )}
          </div>

          {/* Supply Shocks */}
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-blue-500" />
                <div>
                  <h4 className="font-medium">Supply Disruptions</h4>
                  <p className="text-sm text-muted-foreground">
                    Material shortages and delivery delays
                  </p>
                </div>
              </div>
              <Switch
                checked={eventSettings.supplyShocks.enabled}
                onCheckedChange={(checked) =>
                  setEventSettings({
                    ...eventSettings,
                    supplyShocks: { ...eventSettings.supplyShocks, enabled: checked },
                  })
                }
              />
            </div>
            {eventSettings.supplyShocks.enabled && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Probability</Label>
                  <span className="text-sm text-muted-foreground">
                    {(eventSettings.supplyShocks.probability * 100).toFixed(1)}% per hour
                  </span>
                </div>
                <Slider
                  value={[eventSettings.supplyShocks.probability]}
                  onValueChange={([value]) =>
                    setEventSettings({
                      ...eventSettings,
                      supplyShocks: { ...eventSettings.supplyShocks, probability: value },
                    })
                  }
                  min={0}
                  max={0.1}
                  step={0.005}
                />
              </div>
            )}
          </div>

          {/* Demand Spikes */}
          <div className="p-4 border border-border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-green-500" />
                <div>
                  <h4 className="font-medium">Demand Spikes</h4>
                  <p className="text-sm text-muted-foreground">
                    Sudden increases in customer orders
                  </p>
                </div>
              </div>
              <Switch
                checked={eventSettings.demandSpikes.enabled}
                onCheckedChange={(checked) =>
                  setEventSettings({
                    ...eventSettings,
                    demandSpikes: { ...eventSettings.demandSpikes, enabled: checked },
                  })
                }
              />
            </div>
            {eventSettings.demandSpikes.enabled && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Probability</Label>
                  <span className="text-sm text-muted-foreground">
                    {(eventSettings.demandSpikes.probability * 100).toFixed(1)}% per hour
                  </span>
                </div>
                <Slider
                  value={[eventSettings.demandSpikes.probability]}
                  onValueChange={([value]) =>
                    setEventSettings({
                      ...eventSettings,
                      demandSpikes: { ...eventSettings.demandSpikes, probability: value },
                    })
                  }
                  min={0}
                  max={0.15}
                  step={0.01}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save feedback */}
      {saveMessage && (
        <p className={`text-sm mb-4 ${saveMessage.startsWith('Settings saved') ? 'text-green-600' : 'text-destructive'}`}>
          {saveMessage}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" onClick={handleResetDefaults}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
